/**
 * reinsertModuleIntoLiveDoc — rewrite a single module's section(s) in the live
 * Word document from the server's current persisted draft.
 *
 * Used after a regulatory audit: the audit mutates the persisted ``draft_full``
 * (inserting ``[REGULATOR FLAG: …]`` markers at the anchored paragraphs) but the
 * open document is not touched. Calling this re-projects the module's sections so
 * the markers appear inline at the right location — which also lets the review
 * tab's jump-to-issue (``selectTextMarker``) find them.
 *
 * Mirrors the per-module pass of ``loadRunIntoLiveDoc`` /
 * ``useWordWriteQueue.handleProgressEvent`` exactly (sanitize →
 * splitDraftBySections → insertSectionContent → citation pipeline) so the result
 * is identical to a freshly-streamed section. Unlike ``loadRunIntoLiveDoc`` it
 * does NOT scaffold or clear the body — it edits one module in place.
 *
 * Best-effort + non-destructive: ``insertSectionContent`` is a no-op (returns
 * false) when Office isn't available or the heading isn't present, so when no
 * document is loaded this resolves to ``{ sectionsWritten: 0 }`` without error.
 */

import { getSectionContent } from "../../api/client";
import { splitDraftBySections } from "../../services/formatter/sectionSplitter";
import { insertSectionContent, stripRegulatorFlagsInTableCells } from "../../services/wordWriter";
import { runPostSectionCitationPipeline } from "./citationPipeline";
import { sanitizeDraftForWord, stripRegulatorMarkers } from "./draftSanitizer";

export interface ReinsertModuleResult {
  sectionsWritten: number;
}

export interface ReinsertModuleOptions {
  /** Strip every ``[REGULATOR FLAG: …]`` marker (and the trailing
   *  recommendations block) from the projected draft. Used for the "clean
   *  slate" re-projection right before a re-audit — the server draft still
   *  carries the old flags until the audit's own strip-and-reinsert runs. */
  stripRegulatorFlags?: boolean;
  /** Custom-submission override: the writer's REAL heading number for this
   *  module. A custom module's server ``section_number`` is the synthetic
   *  ``CUSTOM-N`` id, which never matches a heading in the writer's document,
   *  so the default number-keyed insert writes 0 sections and the audit markers
   *  never appear (then reconcile auto-resolves them). When set, the module's
   *  single body is written at THIS heading number instead — bypassing the
   *  ``## N.x`` split (a custom module is one heading, its ``draft_full`` is the
   *  body). Safe only after the live→SSOT sync, because it REPLACES the body. */
  customSectionNumber?: string;
  /** Custom-submission override: the writer's heading TITLE. A custom heading
   *  renders its title with NO leading number and no bookmark, so the number
   *  alone (``customSectionNumber``) often can't locate it — the title is passed
   *  to ``insertSectionContent`` (and the table strip) as the resolver that
   *  actually finds the heading. Undefined for template runs. */
  customSectionTitle?: string;
}

export async function reinsertModuleIntoLiveDoc(
  runId: string,
  moduleId: string,
  options: ReinsertModuleOptions = {},
): Promise<ReinsertModuleResult> {
  const resp = await getSectionContent(runId, moduleId);
  const sections = resp?.sections ?? [];

  // Every numbered section this module declares — used to distribute the draft's
  // "## N.x" blocks into the numbered skeleton the same way the live path does.
  const allNums = sections
    .map((s) => s.section_number)
    .filter((n): n is string => !!n && n !== "TITLE");

  const writeJobs: Array<{ secNum: string; chunk: string; secTitle?: string }> = [];

  // Custom-submission fast path: one module == one real heading. The server
  // ``section_number`` is ``CUSTOM-N`` (never matches a heading), so target the
  // writer's real heading (by number AND title — a custom heading often carries
  // no number, so the title is what actually resolves it) and write the whole
  // (marker-annotated) body there, skipping the ``## N.x`` split which a custom
  // body doesn't carry.
  if (options.customSectionNumber || options.customSectionTitle) {
    for (const sec of sections) {
      if (!sec.draft_content) continue;
      const source = options.stripRegulatorFlags
        ? stripRegulatorMarkers(sec.draft_content)
        : sec.draft_content;
      const cleaned = sanitizeDraftForWord(source);
      if (!cleaned) continue;
      writeJobs.push({
        secNum: options.customSectionNumber ?? "",
        secTitle: options.customSectionTitle,
        chunk: cleaned,
      });
    }
  } else {
    for (const sec of sections) {
      if (!sec.section_number || sec.section_number === "TITLE" || !sec.draft_content) continue;
      const source = options.stripRegulatorFlags
        ? stripRegulatorMarkers(sec.draft_content)
        : sec.draft_content;
      const cleaned = sanitizeDraftForWord(source);
      if (!cleaned) continue;
      const chunks = Object.entries(
        splitDraftBySections(cleaned, allNums, sec.section_number),
      ).filter(([, c]) => c.trim().length > 0);
      for (const [secNum, chunk] of chunks) {
        writeJobs.push({ secNum, chunk });
      }
    }
  }

  let written = 0;
  for (const { secNum, chunk, secTitle } of writeJobs) {
    try {
      const ok = await insertSectionContent({
        sectionNumber: secNum,
        sectionTitle: secTitle,
        content: chunk,
        scrollAfterInsert: false,
      });
      if (ok) {
        written += 1;
        // Re-wrap citation markers so the rewritten section behaves identically
        // to a freshly-streamed one (clickable ``[N]``). Swallows its own errors.
        await runPostSectionCitationPipeline(secNum);
      }
    } catch (err) {
      console.warn(
        `[reinsertModuleIntoLiveDoc] insertSectionContent failed for §${secNum} (non-fatal):`,
        err,
      );
    }
  }

  // The section re-render above clears inline PROSE flags but never touches
  // table content (its paragraph-delete boundary would corrupt tables), so a
  // ``[REGULATOR FLAG: …]`` sitting in a table cell survives it. On the
  // clean-slate strip pass, sweep those cell markers out too so the audit
  // starts from a doc with NO regulatory flags — including inside tables.
  const isCustom = !!(options.customSectionNumber || options.customSectionTitle);
  const tableStripTargets = isCustom
    ? [{ number: options.customSectionNumber, title: options.customSectionTitle }]
    : allNums;
  if (options.stripRegulatorFlags && tableStripTargets.length > 0) {
    try {
      await stripRegulatorFlagsInTableCells(tableStripTargets);
    } catch (err) {
      console.warn("[reinsertModuleIntoLiveDoc] table-cell flag strip failed (non-fatal):", err);
    }
  }

  return { sectionsWritten: written };
}
